import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";

export interface CommunityIssueActivityRow {
  issue_id: number;
  issue_name: string;
  issue_cname: string | null;
  activity_total: number;
  comment_count: number;
  post_count: number;
  relation_count: number;
  has_solution: boolean;
  has_action: boolean;
}

export interface CommunityLeaderboardRow {
  user_id: number;
  username: string;
  comment_count: number;
  post_count: number;
  ad_break_count: number;
  award_receive_count: number;
  activity_total: number;
}

export interface CommunityAlignmentDimension {
  dimension: string;
  alignment_count: number;
  avg_response: number | null;
  avg_importance: number | null;
  score: number;
}

export interface CommunityRadarPoint {
  key: string;
  value: number;
}

export interface CommunityAlignmentRollup {
  window_days: number;
  alignment_count: number;
  avg_response: number | null;
  avg_importance: number | null;
  dimensions: CommunityAlignmentDimension[];
  radar: CommunityRadarPoint[];
}

export interface CommunityIssueHealth {
  active_issue_count: number;
  issues_without_solution_count: number;
  issues_without_action_count: number;
  issues_without_solution_or_action_count: number;
}

export interface CommunityCountryBucket {
  country_code: string;
  user_count: number;
}

export interface CommunityDemographics {
  buckets: CommunityCountryBucket[];
  users_with_public_country: number;
  users_unknown_country: number;
}

export interface CommunityOverviewDelta {
  active_issues: number;
  active_contributors: number;
  total_engagements: number;
  pipeline_health_pct: number;
  active_teabates: number;
  governance_score: number;
}

export interface CommunityOverview {
  active_issues: number;
  active_contributors: number;
  total_engagements: number;
  pipeline_health_pct: number;
  active_teabates: number;
  governance_score: number;
  delta: CommunityOverviewDelta;
  window_days: number;
}

export interface CommunityPipeline {
  total_issues: number;
  with_analysis: number;
  with_strategy: number;
  with_execution: number;
  fully_covered: number;
}

export interface CommunityActivityBucket {
  date: string;
  contributions: number;
  analysis: number;
  governance: number;
  total: number;
}

export interface CommunityActivityTrend {
  window_days: number;
  granularity: "day" | "week";
  buckets: CommunityActivityBucket[];
}

export interface CommunityGovernanceFeedbackBucket {
  status: string;
  count: number;
}

/**
 * Shape of the teabate participation block in the governance response.
 * The field on the response is now `teabates`; older backend builds may still
 * emit it under the legacy key `debates` (kept temporarily on the type so the
 * UI tolerates a deploy where backend/frontend cross over).
 */
export interface CommunityGovernanceTeabates {
  total_active_teabates: number;
  new_in_window: number;
  alignment_participants: number;
  total_alignments: number;
}

export interface CommunityGovernance {
  feedback: {
    pipeline: CommunityGovernanceFeedbackBucket[];
    total_open: number;
    new_in_window: number;
    response_rate_pct: number;
  };
  teabates: CommunityGovernanceTeabates;
  /**
   * @deprecated Legacy alias for `teabates`; emitted by backend builds prior to
   * the 2026-05-15 rename. New code should read `teabates` and use
   * `resolveCommunityTeabates(...)` as a defensive bridge during deploys.
   */
  debates?: CommunityGovernanceTeabates;
  reports: {
    total_in_window: number;
    pending: number;
    resolved: number;
    resolution_rate_pct: number;
  };
  sentiment: {
    participants: number;
    total_responses: number;
    positive: number;
    neutral: number;
    negative: number;
  };
  governance_score: number;
  window_days: number;
}

/**
 * Public community dashboard aggregates (read-only).
 */
export const communityApi = {
  async getOverview(params?: { days?: number }): Promise<CommunityOverview> {
    const response = await api.get<{ data: CommunityOverview }>(apiRoutes.community.overview, {
      params: { days: params?.days },
    });
    return response.data.data;
  },

  async getPipeline(): Promise<CommunityPipeline> {
    const response = await api.get<{ data: CommunityPipeline }>(apiRoutes.community.pipeline);
    return response.data.data;
  },

  async getActivityTrend(params?: { days?: number }): Promise<CommunityActivityTrend> {
    const response = await api.get<{ data: CommunityActivityTrend }>(apiRoutes.community.activityTrend, {
      params: { days: params?.days },
    });
    return response.data.data;
  },

  async getGovernanceHealth(params?: { days?: number }): Promise<CommunityGovernance> {
    const response = await api.get<{ data: CommunityGovernance }>(apiRoutes.community.governance, {
      params: { days: params?.days },
    });
    return response.data.data;
  },

  async getIssueActivity(params?: { days?: number; limit?: number }): Promise<CommunityIssueActivityRow[]> {
    const response = await api.get<{ data: CommunityIssueActivityRow[] }>(apiRoutes.community.issueActivity, {
      params: { days: params?.days, limit: params?.limit },
    });
    return response.data.data;
  },

  async getLeaderboard(params?: { days?: number; limit?: number }): Promise<CommunityLeaderboardRow[]> {
    const response = await api.get<{ data: CommunityLeaderboardRow[] }>(apiRoutes.community.leaderboard, {
      params: { days: params?.days, limit: params?.limit },
    });
    return response.data.data;
  },

  async getAlignment(params?: { days?: number }): Promise<CommunityAlignmentRollup> {
    const response = await api.get<{ data: CommunityAlignmentRollup }>(apiRoutes.community.alignment, {
      params: { days: params?.days },
    });
    return response.data.data;
  },

  async getIssueHealth(): Promise<CommunityIssueHealth> {
    const response = await api.get<{ data: CommunityIssueHealth }>(apiRoutes.community.issueHealth);
    return response.data.data;
  },

  async getDemographics(params?: { limit?: number }): Promise<CommunityDemographics> {
    const response = await api.get<{ data: CommunityDemographics }>(apiRoutes.community.demographics, {
      params: { limit: params?.limit },
    });
    return response.data.data;
  },
};
